<template>
    <div :class="[$style['mds-stepper'], classObject, sizeClass]">
        <div :class="$style['mds-stepper__active-step-summary']">
            <span :class="$style['mds-stepper__active-step-label']">
                {{ activeStep ? activeStep.title : '' }}
            </span>
        </div>
        <ul :class="$style['mds-stepper__list']">
            <li :class="$style['mds-stepper__list-item']" v-for="(step, index) in stepsArray" :key="step.id">
                <div
                    :class="[
                        $style['mds-stepper__step'],
                        step.active ? $style['mds-stepper__step--active'] : '',
                        step.steps ? $style['mds-stepper__step--has-small-step'] : '',
                        isComplete(stepsArray, step, index) ? $style['mds-stepper__step--complete'] : '',
                    ]"
                >
                    <a
                        :href="getHref(step)"
                        :id="step.id"
                        :title="step.title"
                        :class="$style['mds-stepper__step-indicator']"
                        aria-current="page"
                        @click.prevent="handleClick(step)"
                    >
                        <svg
                            :class="$style['mds-stepper__step-complete-icon']"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="-0.044 0.062 9.211 8.841"
                        >
                            <path fill="none" stroke="#FFF" stroke-width="2" d="M.643 4.653L3.535 7.39 8.355.642" />
                        </svg>
                        <span :class="$style['mds-stepper__step-label']">{{ step.title }}</span>
                    </a>
                    <div v-if="step.steps" :class="$style['mds-stepper__small-steps']">
                        <div
                            v-for="(smallStep, i) in step.steps"
                            :key="smallStep.id"
                            :class="[
                                $style['mds-stepper__step'],
                                $style['mds-stepper__step--small-step'],
                                smallStep.active ? $style['mds-stepper__step--active'] : '',
                                isComplete(step.steps, smallStep, i) || isComplete(stepsArray, step, index)
                                    ? $style['mds-stepper__step--complete']
                                    : '',
                            ]"
                        >
                            <span v-if="disableInteractiveSmallSteps" :class="$style['mds-stepper__step-indicator']">
                                <span :class="$style['mds-stepper__step-label']">{{ smallStep.title }}</span>
                            </span>

                            <a
                                v-else
                                :id="smallStep.id"
                                :href="getHref(smallStep)"
                                :title="smallStep.title"
                                :class="$style['mds-stepper__step-indicator']"
                                @click.prevent="handleClick(smallStep)"
                            >
                                <span :class="$style['mds-stepper__step-label']">{{ smallStep.title }}</span>
                            </a>
                            <span
                                :class="[
                                    $style['mds-stepper__step-progress-track'],
                                    $style['mds-stepper__step-progress-track--small-step-left'],
                                ]"
                            ></span>
                            <span
                                :class="[
                                    $style['mds-stepper__step-progress-track'],
                                    $style['mds-stepper__step-progress-track--small-step-right'],
                                ]"
                            ></span>
                        </div>
                    </div>
                    <span
                        v-if="!step.steps"
                        :class="[
                            $style['mds-stepper__step-progress-track'],
                            $style['mds-stepper__step-progress-track--large-step'],
                        ]"
                    ></span>
                </div>
                <a
                    :href="getHref(step)"
                    @click.prevent="handleClick(step)"
                    :class="$style['mds-stepper__list-item-label']"
                    tabindex="-1"
                    >{{ step.title }}</a
                >
            </li>
        </ul>
    </div>
</template>

<script>
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsStepper',
        props: {
            hideSmallSteps: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, suppresses the display of small steps between large steps.',
                },
            },
            disableAnimation: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, step progression will not be visually animated.',
                },
            },
            disableInteractiveSmallSteps: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, small steps between each large step will not be clickable.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            responsive: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, renders the responsive variation of the stepper.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the stepper.',
                },
            },
            steps: {
                type: Array,
                required: true,
                validator(value) {
                    return !value[value.length - 1].hasOwnProperty('steps');
                },
                docs: {
                    validation: '**Required**',
                    description: 'An array of step objects. The last step in the set cannot contain small steps.',
                    support: ['v-model'],
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
            },
        },
        model: {
            prop: 'steps',
        },
        data() {
            return {
                stepsArray: this.steps,
                clicked: false,
            };
        },
        mounted() {
            this.emitActiveEvent();
        },
        computed: {
            activeStep() {
                return this.stepsArray.find(obj => {
                    return obj.active === true;
                });
            },
            activeStepIndex() {
                return this.stepsArray.findIndex(obj => {
                    return obj.active === true;
                });
            },
            activeSmallStep() {
                return this.activeStep && this.activeStep.steps
                    ? this.activeStep.steps.find(obj => {
                          return obj.active === true;
                      })
                    : null;
            },
            activeSmallStepIndex() {
                return this.activeStep && this.activeStep.steps
                    ? this.activeStep.steps.findIndex(obj => {
                          return obj.active === true;
                      })
                    : null;
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-stepper--responsive']] = this.responsive;
                obj[this.$style['mds-stepper--no-small-step-indicators']] = this.hideSmallSteps;
                obj[this.$style['mds-stepper--no-animation']] = this.disableAnimation;
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-stepper--clicked']] = this.clicked;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-stepper--${this.size}`];
            },
        },
        watch: {
            stepsArray: {
                handler() {
                    // emitting the event on steps change returning step and smallstep if active
                    this.emitActiveEvent();
                },
                deep: true,
            },
        },
        methods: {
            emitActiveEvent() {
                this.$emit('mds-step-activated', {
                    step: this.activeStep,
                    smallStep: this.activeSmallStep,
                });
            },
            getHref(step) {
                return step.href ? step.href : `#${step.title.replace(' ', '-').toLowerCase()}`;
            },
            isComplete(steps, step, index) {
                if (step.active) {
                    return false;
                }

                const activeIndex = steps.findIndex(obj => {
                    return obj.active === true;
                });
                return index < activeIndex;
            },
            deactivateSmallStep() {
                if (this.stepsArray[this.activeStepIndex] && this.stepsArray[this.activeStepIndex].steps) {
                    this.stepsArray[this.activeStepIndex].steps.forEach(step => {
                        this.$set(step, 'active', false);
                    });
                }
            },
            // checks if one of the smallsteps was clicked
            hasSmallStepClicked(step, obj) {
                return step.steps ? step.steps.find(smallStep => smallStep.id === obj.id) : false;
            },
            setActiveStep(obj) {
                // deactivate current active step's smallSteps, if it has
                this.deactivateSmallStep();
                this.stepsArray.forEach(step => {
                    // automatically assign value to the step, whether is active or not
                    this.$set(step, 'active', step.id === obj.id);
                    // only update step's smallsteps if one of them is the clicked one
                    if (step.steps && this.hasSmallStepClicked(step, obj)) {
                        step.steps.forEach(smallStep => {
                            if (smallStep.id === obj.id) {
                                this.$set(step, 'active', true);
                                this.$set(smallStep, 'active', true);
                            } else {
                                this.$set(smallStep, 'active', false);
                            }
                        });
                    }
                });
            },
            handleClick(step) {
                this.clicked = true;
                this.setActiveStep(step);
                this.$emit('mds-step-clicked', step);
                setTimeout(() => {
                    this.clicked = false;
                }, 200);
            },
        },
    };
</script>

<style lang="scss" module>
    @import './stepper.scss';
</style>
